import React, { forwardRef, useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface DatePickerProps {
  value?: string;
  defaultValue?: string;
  onChange?: (date: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  format?: string;
  minDate?: string;
  maxDate?: string;
}

const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  ({ 
    value,
    defaultValue,
    onChange,
    placeholder = "Select date",
    disabled = false,
    className,
    label,
    error,
    helperText,
    size = 'md',
    format = 'YYYY-MM-DD',
    minDate,
    maxDate,
    ...props 
  }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(value || defaultValue || '');
    const [viewDate, setViewDate] = useState(() => {
      const date = value || defaultValue || new Date().toISOString().split('T')[0];
      return new Date(date);
    });
    
    const containerRef = useRef<HTMLDivElement>(null);

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-3 py-2 text-sm',
      lg: 'px-4 py-3 text-base'
    };

    const baseClasses = 'block w-full rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed';
    
    const stateClasses = error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';

    useEffect(() => {
      if (value !== undefined) {
        setSelectedDate(value);
        if (value) {
          setViewDate(new Date(value));
        }
      }
    }, [value]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    const formatDisplayDate = (dateStr: string) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        weekday: "short",
      });
    };

    const handleDateSelect = (dateStr: string) => {
      setSelectedDate(dateStr);
      onChange?.(dateStr);
      setIsOpen(false);
    };

    const handleClear = () => {
      setSelectedDate('');
      onChange?.(null);
    };

    const generateCalendar = () => {
      const year = viewDate.getFullYear();
      const month = viewDate.getMonth();
      const firstDayOfMonth = new Date(year, month, 1);
      const lastDayOfMonth = new Date(year, month + 1, 0);
      const firstDayOfWeek = firstDayOfMonth.getDay();
      const daysInMonth = lastDayOfMonth.getDate();
      
      const days = [];
      
      // Add empty cells for days before the first day of the month
      for (let i = 0; i < firstDayOfWeek; i++) {
        days.push(null);
      }
      
      // Add days of the month
      for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        days.push(dateStr);
      }
      
      return days;
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
      const newDate = new Date(viewDate);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      setViewDate(newDate);
    };

    const isDateDisabled = (dateStr: string) => {
      if (!dateStr) return false;
      if (minDate && dateStr < minDate) return true;
      if (maxDate && dateStr > maxDate) return true;
      return false;
    };

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        
        <div ref={containerRef} className="relative">
          <div
            className={clsx(
              baseClasses,
              stateClasses,
              sizeClasses[size],
              'cursor-pointer flex items-center justify-between',
              disabled && 'cursor-not-allowed',
              className
            )}
            onClick={() => !disabled && setIsOpen(!isOpen)}
          >
            <span className={clsx(!selectedDate && 'text-gray-400')}>
              {selectedDate ? formatDisplayDate(selectedDate) : placeholder}
            </span>
            <div className="flex items-center space-x-1">
              {selectedDate && !disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              )}
              <CalendarIcon className="h-4 w-4 text-gray-400" />
            </div>
          </div>

          {isOpen && (
            <div className="absolute z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-80">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateMonth('prev');
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <h3 className="font-semibold">
                  {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
                </h3>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateMonth('next');
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>

              {/* Week days */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map(day => (
                  <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {generateCalendar().map((dateStr, index) => (
                  <button
                    type="button"
                    key={index}
                    className={clsx(
                      'h-8 w-8 text-sm rounded hover:bg-blue-100 transition-colors',
                      !dateStr && 'invisible',
                      dateStr === selectedDate && 'bg-blue-600 text-white hover:bg-blue-700',
                      dateStr && dateStr !== selectedDate && 'text-gray-700',
                      dateStr && isDateDisabled(dateStr) && 'text-gray-300 cursor-not-allowed hover:bg-transparent'
                    )}
                    onClick={() => dateStr && !isDateDisabled(dateStr) && handleDateSelect(dateStr)}
                    disabled={!dateStr || isDateDisabled(dateStr)}
                  >
                    {dateStr ? new Date(dateStr).getDate() : ''}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
        
        {helperText && !error && (
          <p className="mt-1 text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    );
  }
);

DatePicker.displayName = 'DatePicker';

export default DatePicker; 